import ClickableContainer from 'components/clickable-container';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { getSkillTag } from 'redux/selectors/video-practice';
import NvIcon from 'shared/components/nv-icon';
import NvLoadingPlaceholder, { PlaceholderType, LineSize, LineHeight } from 'shared/components/nv-loading-placeholder';
import NvStarRating from 'shared/components/nv-star-rating';
import NvTooltip from 'shared/components/nv-tooltip';
import { last, isFinite } from 'underscore';
import { GET_FEEDBACK_PAGE_SIZE } from '../shared/practice-submission';
import ViewFeedback from './view-feedback';

export interface RatingSummaryProps {
  submissionId: number
  totalRatings: number
  skillRating: number
  skillTagId: number
  skillTaggingId: number
  onGetSkillsFeedback: (afterId?: number) => void
  onDeleteFeedback: (id: number) => void
  canDelete?: boolean
}

const RatingSummary = ({
  submissionId,
  skillTaggingId,
  totalRatings,
  skillRating,
  onGetSkillsFeedback,
  skillTagId,
  onDeleteFeedback,
  canDelete,
}: RatingSummaryProps) => {
  const skillTag = useSelector(state => getSkillTag(state, skillTaggingId));
  const skillRatings = useSelector(state => state.app.videoPracticeSubmissions?.[submissionId]?.skillTaggings?.[skillTaggingId]);

  const [showFeedback, setShowFeedback] = useState(false);
  const [viewLess, setViewLess] = useState(false);

  useEffect(() => {
    if (showFeedback
        && !skillRatings?.onceLoaded
        && !skillRatings?.loading
        && totalRatings > 0) {
      onGetSkillsFeedback();
    }
  }, [
    onGetSkillsFeedback, skillRatings?.loading,
    skillRatings?.onceLoaded, showFeedback, totalRatings,
  ]);

  const onViewMore = () => {
    if (skillRatings?.ratings.length === skillRatings.totalRecords) {
      setViewLess(false);
    } else {
      onGetSkillsFeedback(last(skillRatings?.ratings));
    }
  };
  const ratingsToDisplay = viewLess ? skillRatings?.ratings?.slice(0, GET_FEEDBACK_PAGE_SIZE) : skillRatings?.ratings;
  let ratingRounded = Number(skillRating) % 1 !== 0 ? Number(skillRating).toFixed(1) : skillRating;
  ratingRounded = (Number(ratingRounded) * 100) / 100; // To convert 3.0 to 3

  return (
    <div className='d-flex flex-column'>
      <div className='d-flex justify-content-between mb-4'>
        <div className='tag-badge bg-gray-7 py-2 px-3 text-black text-small font-weight-bold'>
          {skillTag?.name}
        </div>
        <div className='d-flex align-items-center'>
          <div className='text-large-regular gray-2 mr-4'>
            {t.PRACTICE_ROOM.SKILL_FEEDBACK.TOTAL_RATINGS(totalRatings)}
          </div>
          <ClickableContainer
            onClick={() => {
              setShowFeedback(show => !show);
            }}
          >
            <NvTooltip
              text={t.PRACTICE_ROOM.SKILL_FEEDBACK.RATING_TOOLTIP(ratingRounded.toString(), '5')}
              placement='top'
            >
              <div>
                <NvStarRating readonly ratedValue={ratingRounded} starSize='medium' />
              </div>
            </NvTooltip>
            <NvIcon
              size='xss-smallest'
              icon={showFeedback ? 'arrow-up' : 'arrow-down'}
              className={`${!totalRatings ? 'invisible' : ''} d-flex align-items-center ml-4 gray-2`}
            />
          </ClickableContainer>
        </div>
      </div>
      {showFeedback && (
        <React.Fragment>
          {skillRatings?.ratings?.length > 0 && (
            <div className='mt-4'>
              {ratingsToDisplay.map(rating => (
                <ViewFeedback
                  key={rating}
                  ratingId={rating}
                  onDeleteFeedback={onDeleteFeedback}
                  canDelete={canDelete}
                />
              ))}
              {((skillRatings?.onceLoaded
                && skillRatings?.hasMore
                && !skillRatings.loading
              ) || viewLess) && (
                <ClickableContainer
                  className='text-primary justify-content-center mb-6'
                  onClick={() => onViewMore()}
                >{t.SHARED.VIEW_MORE()}
                </ClickableContainer>
              )}
              {!skillRatings?.loading
              && ratingsToDisplay.length > GET_FEEDBACK_PAGE_SIZE
              && skillRatings?.totalRecords === skillRatings?.ratings?.length
              && (
                <ClickableContainer
                  className='text-primary justify-content-center mb-6'
                  onClick={() => setViewLess(true)}
                >{t.SHARED.VIEW_LESS()}
                </ClickableContainer>
              )}
            </div>
          )}
          {skillRatings?.loading && (
            <React.Fragment>
              <NvLoadingPlaceholder type={PlaceholderType.LINE} size={LineSize.FULL} height={LineHeight.QUARTER_SPACING} />
              <NvLoadingPlaceholder type={PlaceholderType.LINE} size={LineSize.FULL} height={LineHeight.QUARTER_SPACING} />
              <NvLoadingPlaceholder type={PlaceholderType.LINE} size={LineSize.MEDIUM} height={LineHeight.QUARTER_SPACING} />
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default RatingSummary;
